import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Heading, LoadingOrbit, Text, TextField, Select, useToast, TagBadge, type SelectOption } from '@chatsdet/ui';
import {
  Cloud,
  Plus,
  Trash,
  CheckCircle,
  Warning,
  ArrowsClockwise,
  X,
  Copy,
  MapPin,
} from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { BRAND } from '../../../brand';

type AwsConnection = {
  id: string;
  name: string;
  roleArn: string;
  externalId: string;
  region: string;
  accountId: string | null;
  lastValidatedAt: string | null;
  createdAt: string;
};

type AwsValidationResult = {
  ok: boolean;
  identity?: { arn: string; account: string };
  services: Record<string, { accessible: boolean; error?: string; count?: number }>;
  error?: string;
  validatedAt: string;
};

const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  codepipeline: 'CodePipeline',
  codebuild: 'CodeBuild',
  cloudwatch: 'CloudWatch',
  logs: 'CloudWatch Logs',
};

function serviceDisplayName(key: string): string {
  return SERVICE_DISPLAY_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

type AwsConnectionDetail = AwsConnection & {
  lastValidationResult: AwsValidationResult | null;
  trustPolicy: Record<string, unknown> | null;
  minimumPermissionsPolicy: Record<string, unknown>;
};

type AwsConnectionSectionProps = {
  isAdmin: boolean;
};

const AWS_REGIONS: SelectOption[] = [
  { id: 'us-east-1', label: 'US East (N. Virginia)' },
  { id: 'us-east-2', label: 'US East (Ohio)' },
  { id: 'us-west-1', label: 'US West (N. California)' },
  { id: 'us-west-2', label: 'US West (Oregon)' },
  { id: 'eu-west-1', label: 'EU (Ireland)' },
  { id: 'eu-west-2', label: 'EU (London)' },
  { id: 'eu-central-1', label: 'EU (Frankfurt)' },
  { id: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { id: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { id: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { id: 'ca-central-1', label: 'Canada (Central)' },
  { id: 'sa-east-1', label: 'South America (São Paulo)' },
];

export function AwsConnectionSection({ isAdmin }: AwsConnectionSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const name = BRAND.name;
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingConnection, setEditingConnection] = React.useState<AwsConnection | null>(null);
  const [validatingId, setValidatingId] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<{
    connectionId: string;
    result: AwsValidationResult;
  } | null>(null);
  const [setupConnection, setSetupConnection] = React.useState<AwsConnectionDetail | null>(null);

  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    roleArn: '',
    region: 'us-east-1',
  });

  // Fetch AWS connections
  const connectionsQuery = useQuery({
    queryKey: ['aws-connections'],
    queryFn: async (): Promise<{ ok: boolean; data: AwsConnection[] }> => {
      const res = await fetch(`${API_URL}/integrations/aws`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch connections');
      }
      return res.json();
    },
    enabled: isAdmin,
  });

  // Create connection
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch(`${API_URL}/integrations/aws`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to create connection');
      }
      return result;
    },
    onSuccess: (result) => {
      toast.success('AWS connection created');
      setIsCreateOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['aws-connections'] });
      // Show setup instructions after creation
      if (result.data) {
        setSetupConnection(result.data);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update connection
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const res = await fetch(`${API_URL}/integrations/aws/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to update connection');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Connection updated');
      setEditingConnection(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['aws-connections'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Delete connection
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/integrations/aws/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        throw new Error(result.error || 'Failed to delete connection');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Connection deleted');
      queryClient.invalidateQueries({ queryKey: ['aws-connections'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Validate connection
  const validateMutation = useMutation({
    mutationFn: async (id: string) => {
      setValidatingId(id);
      setValidationResult(null);
      const res = await fetch(`${API_URL}/integrations/aws/${id}/validate`, {
        method: 'POST',
        credentials: 'include',
      });
      const result = await res.json();
      return { ...result, connectionId: id };
    },
    onSuccess: (result) => {
      setValidatingId(null);
      if (result.ok && result.data) {
        setValidationResult({ connectionId: result.connectionId, result: result.data });
        if (result.data.ok) {
          toast.success('AWS connection validated successfully');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['aws-connections'] });
    },
    onError: (error: Error) => {
      setValidatingId(null);
      toast.error(error.message);
    },
  });

  // Fetch connection detail (for setup dialog)
  const fetchConnectionDetail = React.useCallback(
    async (id: string): Promise<AwsConnectionDetail | null> => {
      const res = await fetch(`${API_URL}/integrations/aws/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const result = await res.json();
      return result.ok ? result.data : null;
    },
    []
  );

  const resetForm = () => {
    setFormData({ name: '', roleArn: '', region: 'us-east-1' });
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (connection: AwsConnection) => {
    setFormData({
      name: connection.name,
      roleArn: connection.roleArn,
      region: connection.region,
    });
    setEditingConnection(connection);
  };

  const handleSubmit = () => {
    if (editingConnection) {
      updateMutation.mutate({ id: editingConnection.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (connection: AwsConnection) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${connection.name}"? This will remove the AWS connection.`
      )
    ) {
      deleteMutation.mutate(connection.id);
    }
  };

  const handleShowSetup = async (connection: AwsConnection) => {
    const detail = await fetchConnectionDetail(connection.id);
    if (detail) {
      setSetupConnection(detail);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const connections = connectionsQuery.data?.data ?? [];
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isDialogOpen = isCreateOpen || !!editingConnection;

  const isFormValid = formData.name.trim() && formData.roleArn.trim();

  if (!isAdmin) {
    return (
      <Card padding="lg">
        <CardContent>
          <Text className="text-muted-foreground">
            Only organization admins can manage AWS connections.
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Box className="space-y-6">
        <Card padding="lg">
          <CardHeader>
            <Flex align="start" gap="md">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Cloud size={24} className="text-primary" weight="bold" />
              </div>
              <div className="flex-1">
                <Heading level={3}>AWS Connections</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Connect your AWS accounts for CI integration (CodePipeline + CodeBuild)
                </Text>
              </div>
              <Button variant="primary" onPress={handleOpenCreate}>
                <Plus size={16} className="mr-2" />
                Add Connection
              </Button>
            </Flex>
          </CardHeader>
          <CardContent>
            {connectionsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-8">
                <LoadingOrbit size="md" />
              </Flex>
            ) : connections.length > 0 ? (
              <div className="space-y-3">
                {connections.map((connection) => (
                  <div key={connection.id} className="space-y-2">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Text weight="medium">{connection.name}</Text>
                          {connection.accountId && (
                            <TagBadge name={connection.accountId} color="gray" hideDot size="sm" />
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <Flex align="center" gap="xs">
                            <MapPin size={14} className="text-muted-foreground" />
                            <Text size="sm" className="text-muted-foreground font-mono">
                              {connection.region}
                            </Text>
                          </Flex>
                          {connection.lastValidatedAt && (
                            <Flex align="center" gap="xs">
                              <CheckCircle size={14} className="text-success" />
                              <Text size="xs" className="text-muted-foreground">
                                Validated{' '}
                                {new Date(connection.lastValidatedAt).toLocaleDateString()}
                              </Text>
                            </Flex>
                          )}
                        </div>
                      </div>
                      <Flex gap="sm">
                        <Button
                          variant="ghost"
                          size="sm"
                          onPress={() => handleShowSetup(connection)}
                        >
                          Setup
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Validate"
                          onPress={() => validateMutation.mutate(connection.id)}
                          isDisabled={validatingId === connection.id}
                        >
                          <ArrowsClockwise
                            size={16}
                            className={validatingId === connection.id ? 'animate-spin' : ''}
                          />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => handleOpenEdit(connection)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Delete"
                          onPress={() => handleDelete(connection)}
                          isDisabled={deleteMutation.isPending}
                        >
                          <Trash size={16} className="text-destructive" />
                        </Button>
                      </Flex>
                    </div>

                    {/* Validation result inline */}
                    {validationResult?.connectionId === connection.id && (
                      <div
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          validationResult.result.ok
                            ? 'bg-success/10 border-success/20'
                            : 'bg-destructive/10 border-destructive/20'
                        }`}
                      >
                        {validationResult.result.ok ? (
                          <CheckCircle
                            size={18}
                            className="text-success shrink-0 mt-0.5"
                            weight="fill"
                          />
                        ) : (
                          <Warning
                            size={18}
                            className="text-destructive shrink-0 mt-0.5"
                            weight="fill"
                          />
                        )}
                        <div className="flex-1 min-w-0 space-y-1">
                          {validationResult.result.error && (
                            <Text
                              size="sm"
                              className={
                                validationResult.result.ok
                                  ? 'text-success'
                                  : 'text-destructive'
                              }
                            >
                              {validationResult.result.error}
                            </Text>
                          )}
                          {validationResult.result.identity && (
                            <Text size="xs" className="text-muted-foreground font-mono">
                              Identity: {validationResult.result.identity.arn}
                            </Text>
                          )}
                          <div className="flex gap-4">
                            {Object.entries(validationResult.result.services).map(
                              ([key, status]) => (
                                <ServiceStatus
                                  key={key}
                                  name={serviceDisplayName(key)}
                                  status={status}
                                />
                              )
                            )}
                          </div>
                        </div>
                        <Button
                          variant="icon"
                          size="sm"
                          iconOnly
                          tooltip="Dismiss"
                          onPress={() => setValidationResult(null)}
                          className="shrink-0"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Cloud size={48} className="mx-auto text-muted-foreground mb-4" weight="light" />
                <Text className="text-muted-foreground">No AWS connections</Text>
                <Text size="sm" className="text-muted-foreground mt-2 max-w-md mx-auto">
                  Connect your AWS account to integrate with CodePipeline and CodeBuild for CI
                  workflows.
                </Text>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it works card */}
        <Card padding="md">
          <CardContent>
            <Flex gap="md" align="start">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
                <Warning size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-3">
                <Text size="sm" weight="medium">
                  How it works
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  {name} connects to your AWS account using IAM cross-account role assumption.
                  You&apos;ll create a connection here first, then use the generated setup
                  instructions to create a matching IAM role in your AWS account.
                </Text>

                <Text size="sm" weight="medium" className="pt-2">
                  Setup Steps
                </Text>
                <div className="text-sm text-muted-foreground space-y-2">
                  <div>
                    <strong>1. Add a connection here first.</strong> Click &quot;Add Connection&quot;
                    above. Choose a name and region. For the Role ARN, enter the ARN of the role
                    you&apos;ll create in the next step &mdash; the format is{' '}
                    <code className="bg-muted px-1 rounded text-xs">
                      arn:aws:iam::{'<'}account-id{'>'}:role/{'<'}role-name{'>'}
                    </code>{' '}
                    (e.g.{' '}
                    <code className="bg-muted px-1 rounded text-xs">
                      arn:aws:iam::123456789012:role/CofactorAccess
                    </code>
                    ).
                  </div>
                  <div>
                    <strong>2. Open the setup dialog.</strong> After creating the connection, a setup
                    dialog appears with the <strong>trust policy</strong> and{' '}
                    <strong>permissions policy</strong> JSON, pre-filled with a unique External ID.
                    You can also re-open this any time by clicking &quot;Setup&quot; on a connection.
                  </div>
                  <div>
                    <strong>3. Create the IAM role in AWS.</strong> In the{' '}
                    <strong>AWS IAM Console</strong>:
                    <ol className="list-decimal list-inside ml-4 mt-1 space-y-1">
                      <li>
                        Go to <strong>IAM {'>'} Roles {'>'} Create role</strong>
                      </li>
                      <li>
                        Select <strong>Custom trust policy</strong> and paste the trust policy from
                        the setup dialog
                      </li>
                      <li>
                        On the permissions step, click <strong>Create policy</strong>, switch to
                        the JSON editor, and paste the permissions policy from the setup dialog
                      </li>
                      <li>
                        Name the role to match your ARN (e.g.{' '}
                        <code className="bg-muted px-1 rounded text-xs">CofactorAccess</code>)
                      </li>
                    </ol>
                  </div>
                  <div>
                    <strong>4. Validate.</strong> Back here, click the validate button to confirm
                    {name} can assume the role and access CodePipeline / CodeBuild.
                  </div>
                </div>
              </div>
            </Flex>
          </CardContent>
        </Card>
      </Box>

      {/* Create/Edit Dialog */}
      <DialogTrigger
        isOpen={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingConnection(null);
            resetForm();
          }
        }}
      >
        <span />
        <Dialog title={editingConnection ? 'Edit AWS Connection' : 'Add AWS Connection'}>
          <DialogContent>
            <Box className="space-y-4">
              <TextField
                label="Name"
                placeholder="e.g., Production AWS, Dev Account"
                value={formData.name}
                onChange={(v) => setFormData((f) => ({ ...f, name: v }))}
                isDisabled={isPending}
                autoFocus
              />
              <TextField
                label="Role ARN"
                placeholder="arn:aws:iam::123456789012:role/CofactorAccess"
                value={formData.roleArn}
                onChange={(v) => setFormData((f) => ({ ...f, roleArn: v }))}
                isDisabled={isPending}
                description={`The ARN of the IAM role ${name} will assume in your account`}
              />
              <Select
                label="Region"
                aria-label="AWS Region"
                selectedKey={formData.region}
                items={AWS_REGIONS}
                onSelectionChange={(key) =>
                  setFormData((f) => ({ ...f, region: key as string }))
                }
                isDisabled={isPending}
              />
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setIsCreateOpen(false);
                setEditingConnection(null);
                resetForm();
              }}
              isDisabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSubmit}
              isDisabled={isPending || !isFormValid}
            >
              {isPending ? 'Saving...' : editingConnection ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Setup Instructions Dialog */}
      <DialogTrigger
        isOpen={!!setupConnection}
        onOpenChange={(open) => {
          if (!open) setSetupConnection(null);
        }}
        size="2xl"
      >
        <span />
        <Dialog title="AWS Connection Setup">
          <DialogContent>
            {setupConnection && (
              <Box className="space-y-5">
                <Text size="sm" className="text-muted-foreground">
                  Follow these steps in the{' '}
                  <strong>AWS IAM Console</strong> to create a role that {name} can assume.
                </Text>

                {/* Step 1: Create permissions policy */}
                <div className="space-y-2">
                  <Flex align="center" gap="xs">
                    <TagBadge name="Step 1" color="gray" hideDot size="sm" />
                    <Text size="sm" weight="medium">
                      Create a permissions policy
                    </Text>
                  </Flex>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>
                      In <strong>IAM {'>'} Policies {'>'} Create policy</strong>, switch to the{' '}
                      <strong>JSON</strong> editor and paste this <strong>permissions policy</strong>. Name it something
                      like{' '}
                      <code className="bg-muted px-1 rounded">CofactorCI</code>.
                    </div>
                  </div>
                  <div className="relative rounded-lg">
                    <div className="px-3 py-1.5 bg-muted/50 border-b border-border rounded-t-lg">
                      <Text size="xs" weight="medium" className="text-muted-foreground">
                        Permissions Policy (paste in IAM {'>'} Policies {'>'} Create policy)
                      </Text>
                    </div>
                    <pre className="p-3 pr-20 text-xs font-mono overflow-x-auto max-h-48 bg-muted rounded-b-lg">
                      {JSON.stringify(setupConnection.minimumPermissionsPolicy, null, 2)}
                    </pre>
                    <div className="absolute top-8 right-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() =>
                          copyToClipboard(
                            JSON.stringify(setupConnection.minimumPermissionsPolicy, null, 2),
                            'Permissions policy'
                          )
                        }
                      >
                        <Copy size={14} className="mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Step 2: Create role with trust policy */}
                <div className="space-y-2">
                  <Flex align="center" gap="xs">
                    <TagBadge name="Step 2" color="gray" hideDot size="sm" />
                    <Text size="sm" weight="medium">
                      Create an IAM role
                    </Text>
                  </Flex>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>
                      In <strong>IAM {'>'} Roles {'>'} Create role</strong>, select{' '}
                      <strong>Custom trust policy</strong> and paste the <strong>trust policy</strong> below.
                      On the next step, search for and attach the{' '}
                      <code className="bg-muted px-1 rounded">CofactorCI</code> policy you created in Step 1. Name the role{' '}
                      <code className="bg-muted px-1 rounded">{setupConnection.roleArn.split('/').pop()}</code>.
                    </div>
                    <div>
                      The trust policy includes a unique External ID (
                      <code className="bg-muted px-1 rounded">{setupConnection.externalId}</code>)
                      for confused deputy protection.
                    </div>
                  </div>
                  {setupConnection.trustPolicy ? (
                    <div className="relative rounded-lg">
                      <div className="px-3 py-1.5 bg-muted/50 border-b border-border rounded-t-lg">
                        <Text size="xs" weight="medium" className="text-muted-foreground">
                          Trust Policy (paste in IAM {'>'} Roles {'>'} Create role {'>'} Custom trust policy)
                        </Text>
                      </div>
                      <pre className="p-3 pr-20 text-xs font-mono overflow-x-auto max-h-48 bg-muted rounded-b-lg">
                        {JSON.stringify(setupConnection.trustPolicy, null, 2)}
                      </pre>
                      <div className="absolute top-8 right-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onPress={() =>
                            copyToClipboard(
                              JSON.stringify(setupConnection.trustPolicy, null, 2),
                              'Trust policy'
                            )
                          }
                        >
                          <Copy size={14} className="mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Text size="xs" className="text-muted-foreground italic">
                      Trust policy unavailable ({name} AWS credentials not configured in this environment)
                    </Text>
                  )}
                </div>

                {/* Step 3: Validate */}
                <div className="space-y-2 pt-2 border-t">
                  <Text size="sm" weight="medium">
                    Step 3: Validate the connection
                  </Text>
                  <Text size="xs" className="text-muted-foreground">
                    Once the IAM role is created in AWS, click below to verify {name} can assume the
                    role and access your CI services.
                  </Text>
                  <Button
                    variant="primary"
                    size="sm"
                    onPress={() => {
                      validateMutation.mutate(setupConnection.id);
                      setSetupConnection(null);
                    }}
                  >
                    <ArrowsClockwise size={16} className="mr-2" />
                    Validate Connection
                  </Button>
                </div>
              </Box>
            )}
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setSetupConnection(null)}>
              Close
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}

/**
 * Inline component showing a service probe result
 */
function ServiceStatus({
  name,
  status,
}: {
  name: string;
  status: { accessible: boolean; error?: string; count?: number };
}) {
  return (
    <Flex align="center" gap="xs">
      {status.accessible ? (
        <CheckCircle size={14} className="text-success" weight="fill" />
      ) : (
        <X size={14} className="text-destructive" />
      )}
      <Text size="xs" className="text-muted-foreground">
        {name}
        {status.accessible && status.count !== undefined ? ` (${status.count})` : ''}
      </Text>
    </Flex>
  );
}
